import { useState, useCallback, useEffect } from "react";
import { costsService } from "@/api/services/costs.service";
import { getDefaultCostData, WORK_DETAILS_CATEGORY_TABS, getGroup6SectionIdPrefixForAccordionItem, getGroup6CategoryFromSectionId, stripGroup6SectionIdPrefix, getMainOtherSectionPrefix, stripMainOtherSectionPrefix, OTHER_ACCORDION_ITEM_ID, OTHER_UNIT_OPTIONS, } from "../costParametersDefaults";
const MAIN_CATEGORY_GROUP_IDS = {
    compliance: "group1",
    service_trades: "group2",
    flooring: "group3",
    internal_demolition: "group4",
    preliminary_cost: "group5",
};
const MAIN_CATEGORIES = [
    "compliance",
    "service_trades",
    "flooring",
    "internal_demolition",
    "preliminary_cost",
];
function isMainCategory(id) {
    return MAIN_CATEGORIES.includes(id);
}
/**
 * Map an API cost item into an editable "Other" sub-item. The backend does
 * not persist unitOptions (it regenerates them from the stored unit enum),
 * so we ignore item.unitOptions and use the canonical list. selectedUnit is
 * kept when it's one of the canonical units, otherwise it falls back so the
 * dropdown never renders blank.
 */
function toOtherSubItem(item) {
    return {
        id: item.id,
        title: item.label,
        unitOptions: OTHER_UNIT_OPTIONS,
        selectedUnit: OTHER_UNIT_OPTIONS.includes(item.selectedUnit)
            ? item.selectedUnit
            : "lump sum",
        rate: item.rate,
        ...(item.hasEmptyUnit !== undefined && {
            hasEmptyUnit: item.hasEmptyUnit,
        }),
    };
}
const groupMapping = {
    group1: ["compliance"],
    group2: ["service_trades"],
    group3: ["flooring"],
    group4: ["internal_demolition"],
    group5: ["preliminary_cost"],
};
export function useCostParametersData(onSuccess, onError) {
    const [accordionData, setAccordionData] = useState(getDefaultCostData);
    const [expandedAccordions, setExpandedAccordions] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const response = await costsService.getGroups();
                if (response.data && response.data.length > 0) {
                    const newData = getDefaultCostData();
                    for (const group of response.data) {
                        if (group.id === "group6") {
                            const group6Items = newData.group6;
                            for (const section of group.sections) {
                                const category = getGroup6CategoryFromSectionId(section.id);
                                if (!category)
                                    continue;
                                const localSectionId = stripGroup6SectionIdPrefix(section.id, category);
                                const accordionItemIdForCategory = category === "other"
                                    ? "other"
                                    : category === "plastering"
                                        ? "plastering"
                                        : null;
                                for (const accordionItem of group6Items) {
                                    if (!accordionItem.sections)
                                        continue;
                                    const matchingSection = accordionItem.sections.find((s) => s.sectionId === localSectionId);
                                    if (!matchingSection)
                                        continue;
                                    // Only update the accordion item that matches this category.
                                    // Both Other and Plastering use sectionId "placeholder", so we must
                                    // match by accordion item id to avoid overwriting Plastering when loading Other.
                                    if (accordionItemIdForCategory &&
                                        accordionItem.id !== accordionItemIdForCategory) {
                                        continue;
                                    }
                                    matchingSection.description = section.description;
                                    if (category === "other") {
                                        // For the Other section, fully restore all items from the
                                        // API so custom additions and label/rate changes round-trip.
                                        // unitOptions is frontend-owned (not persisted by backend).
                                        matchingSection.items = section.items.map(toOtherSubItem);
                                    }
                                    else {
                                        for (const item of section.items) {
                                            const matchingItem = matchingSection.items.find((i) => i.id === item.id);
                                            if (matchingItem) {
                                                matchingItem.rate = item.rate;
                                                matchingItem.selectedUnit = item.selectedUnit;
                                            }
                                        }
                                    }
                                }
                            }
                            continue;
                        }
                        const categories = groupMapping[group.id] || [];
                        for (const category of categories) {
                            if (!newData[category])
                                continue;
                            const otherPrefix = getMainOtherSectionPrefix(category);
                            for (const section of group.sections) {
                                // The editable "Other" section is saved with a category-scoped
                                // prefix and must be fully restored from the API (custom rows,
                                // labels, units), mirroring how group6's "Other" loads.
                                if (section.id.startsWith(otherPrefix)) {
                                    const localSectionId = stripMainOtherSectionPrefix(section.id, category);
                                    const otherItem = newData[category].find((i) => i.id === OTHER_ACCORDION_ITEM_ID);
                                    if (!otherItem?.sections)
                                        continue;
                                    const otherSection = otherItem.sections.find((s) => s.sectionId === localSectionId);
                                    if (!otherSection)
                                        continue;
                                    otherSection.description = section.description;
                                    otherSection.items = section.items.map(toOtherSubItem);
                                    continue;
                                }
                                for (const accordionItem of newData[category]) {
                                    if (!accordionItem.sections)
                                        continue;
                                    // "Other" is handled above via full-restore; never patch it
                                    // through the seeded rate/unit path.
                                    if (accordionItem.id === OTHER_ACCORDION_ITEM_ID)
                                        continue;
                                    const matchingSection = accordionItem.sections.find((s) => {
                                        const sectionIdMatch = s.sectionId === section.id;
                                        const sectionTitleMatch = !section.title || !s.sectionTitle
                                            ? true
                                            : s.sectionTitle === section.title;
                                        return sectionIdMatch && sectionTitleMatch;
                                    });
                                    if (matchingSection) {
                                        matchingSection.description = section.description;
                                        for (const item of section.items) {
                                            const matchingItem = matchingSection.items.find((i) => i.id === item.id);
                                            if (matchingItem) {
                                                matchingItem.rate = item.rate;
                                                matchingItem.selectedUnit = item.selectedUnit;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    setAccordionData(newData);
                }
            }
            catch (err) {
                console.error("Error loading cost parameters:", err);
            }
            finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);
    const updateSectionField = useCallback((category, itemId, sectionIndex, field, value) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[category]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex], [field]: value };
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[category] = categoryItems;
            return newData;
        });
    }, []);
    const toggleAccordion = useCallback((id) => {
        setExpandedAccordions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id))
                newSet.delete(id);
            else
                newSet.add(id);
            return newSet;
        });
    }, []);
    const updateSubItemField = useCallback((category, itemId, sectionIndex, subItemId, field, value) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[category]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            items[subItemIndex] = {
                ...items[subItemIndex],
                [field]: field === "rate"
                    ? typeof value === "number"
                        ? value
                        : parseFloat(value) || 0
                    : value,
            };
            section.items = items;
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[category] = categoryItems;
            return newData;
        });
    }, []);
    const addOtherWorkLabel = useCallback((category) => {
        setAccordionData((prevData) => {
            const categoryItems = [...prevData[category]];
            const otherIndex = categoryItems.findIndex((item) => item.id === OTHER_ACCORDION_ITEM_ID);
            if (otherIndex === -1)
                return prevData;
            const otherItem = { ...categoryItems[otherIndex] };
            if (!otherItem.sections)
                return prevData;
            const sections = [...otherItem.sections];
            const placeholderIndex = sections.findIndex((s) => s.sectionId === "placeholder");
            if (placeholderIndex === -1)
                return prevData;
            const section = { ...sections[placeholderIndex] };
            const newItem = {
                // Category-scoped id so it never collides with another group's
                // custom row (the backend upsert key is not scoped by group).
                id: `other-${category}-custom-${Date.now()}`,
                title: "",
                unitOptions: OTHER_UNIT_OPTIONS,
                selectedUnit: "lump sum",
                rate: 0,
            };
            section.items = [...section.items, newItem];
            sections[placeholderIndex] = section;
            otherItem.sections = sections;
            categoryItems[otherIndex] = otherItem;
            return { ...prevData, [category]: categoryItems };
        });
    }, []);
    const removeOtherWorkLabel = useCallback(async (category, subItemId) => {
        try {
            await costsService.deleteParameter(subItemId);
        }
        catch (err) {
            // A row that was added but never saved has no backend record, so
            // DELETE 404s — treat that as "already gone" / success. Any other
            // failure is a real error.
            const status = err?.response
                ?.status;
            if (status !== 404) {
                onError();
                return;
            }
        }
        setAccordionData((prevData) => {
            const categoryItems = [...prevData[category]];
            const otherIndex = categoryItems.findIndex((item) => item.id === OTHER_ACCORDION_ITEM_ID);
            if (otherIndex === -1)
                return prevData;
            const updatedOther = { ...categoryItems[otherIndex] };
            if (!updatedOther.sections)
                return prevData;
            const sections = [...updatedOther.sections];
            const placeholderIndex = sections.findIndex((s) => s.sectionId === "placeholder");
            if (placeholderIndex === -1)
                return prevData;
            const updatedSection = { ...sections[placeholderIndex] };
            const filteredItems = updatedSection.items.filter((item) => item.id !== subItemId);
            if (filteredItems.length === 0)
                return prevData;
            updatedSection.items = filteredItems;
            sections[placeholderIndex] = updatedSection;
            updatedOther.sections = sections;
            categoryItems[otherIndex] = updatedOther;
            return { ...prevData, [category]: categoryItems };
        });
    }, [onError]);
    const handleSave = useCallback(async () => {
        try {
            setSaving(true);
            for (const category of WORK_DETAILS_CATEGORY_TABS) {
                if (!isMainCategory(category.id))
                    continue;
                const categoryItems = accordionData[category.id];
                const sections = [];
                categoryItems.forEach((accordionItem) => {
                    if (!accordionItem.sections)
                        return;
                    const isOther = accordionItem.id === OTHER_ACCORDION_ITEM_ID;
                    accordionItem.sections.forEach((section) => {
                        sections.push({
                            id: isOther
                                ? getMainOtherSectionPrefix(category.id) + section.sectionId
                                : section.sectionId,
                            title: section.sectionTitle || accordionItem.title,
                            description: section.description,
                            items: section.items.map((item) => ({
                                id: item.id,
                                label: item.title,
                                unitOptions: [...item.unitOptions],
                                selectedUnit: item.selectedUnit,
                                rate: item.rate,
                                hasEmptyUnit: item.hasEmptyUnit,
                            })),
                        });
                    });
                });
                const groupId = MAIN_CATEGORY_GROUP_IDS[category.id];
                await costsService.saveGroupParameters(groupId, sections);
            }
            const group6Sections = [];
            const group6Items = accordionData.group6;
            group6Items.forEach((accordionItem) => {
                if (!accordionItem.sections)
                    return;
                const prefix = getGroup6SectionIdPrefixForAccordionItem(accordionItem.id);
                accordionItem.sections.forEach((section) => {
                    group6Sections.push({
                        id: prefix + section.sectionId,
                        title: section.sectionTitle || accordionItem.title,
                        description: section.description,
                        items: section.items.map((item) => ({
                            id: item.id,
                            label: item.title,
                            unitOptions: [...item.unitOptions],
                            selectedUnit: item.selectedUnit,
                            rate: item.rate,
                            hasEmptyUnit: item.hasEmptyUnit,
                        })),
                    });
                });
            });
            if (group6Sections.length > 0) {
                await costsService.saveGroupParameters("group6", group6Sections);
            }
            onSuccess();
        }
        catch (err) {
            console.error("Error saving cost parameters:", err);
            onError();
        }
        finally {
            setSaving(false);
        }
    }, [accordionData, onSuccess, onError]);
    return {
        accordionData,
        expandedAccordions,
        loading,
        saving,
        toggleAccordion,
        updateSubItemField,
        updateSectionField,
        addOtherWorkLabel,
        removeOtherWorkLabel,
        handleSave,
        setExpandedAccordions,
    };
}
